import { selectCurrency } from '@/redux/config/configSlice';
import { DiscountType } from '@/redux/data/dataTypes';
import { useAppSelector } from '@/redux/hooks';
import { DollarOutlined, PercentageOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface DiscountItemProps {
  discount: DiscountType;
  onItemPress?: (discount: DiscountType) => void;
  layout?: 'grid' | 'compact';
}

const DiscountItem: FC<DiscountItemProps> = ({ discount, onItemPress, layout = 'grid' }) => {
  const { i18n } = useTranslation();
  const isPercentage = discount.type === 'PERCENT';
  const currency = useAppSelector(selectCurrency);
  const lang = i18n.language;
  const isArabic = lang === 'ar';

  const isCompact = layout === 'compact';

  return (
    <div
      onClick={() => onItemPress?.(discount)}
      className={`group flex items-center gap-2.5 cursor-pointer transition-all duration-200 
        bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600
        rounded-xl shadow-sm hover:shadow-md
        ${isCompact ? 'p-1.5 m-0.5' : 'p-2.5 m-0.5'}`}
    >
      {/* Icon */}
      <div
        className={`flex items-center justify-center flex-shrink-0 rounded-lg
          bg-[#e6f7ff] dark:bg-gray-500 text-frenchBlue dark:text-white 
          transition-transform duration-200 group-hover:scale-105
          ${isCompact ? 'w-7 h-7 text-sm' : 'w-9 h-9 text-base'}`}
      >
        {isPercentage ? <PercentageOutlined /> : <DollarOutlined />}
      </div>

      {/* Content */}
      <div className="flex items-center justify-between flex-1">
        {/* Name + Type */}
        <div className="flex flex-col flex-1 overflow-hidden">
          <div
            className={`truncate font-semibold text-gray-900 dark:text-white
              group-hover:text-frenchBlue dark:group-hover:text-white
              transition-colors duration-200 
              ${isCompact ? 'text-xs' : 'text-sm'}`}
          >
            {isArabic && discount.nameAr ? discount.nameAr : discount.name}
          </div>
          <div
            className={`text-gray-500 dark:text-gray-300 truncate
              ${isCompact ? 'text-[10px]' : 'text-xs'}`}
          >
            {isPercentage ? 'Percentage discount' : 'Fixed amount discount'}
          </div>
        </div>

        {/* Value */}
        <div className="flex-shrink-0 ml-2.5">
          <div
            className={`rounded-lg bg-gray-100 dark:bg-gray-500 
              text-center whitespace-nowrap
              ${isCompact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2.5 py-0.5 text-xs'}`}
          >
            <span className="font-bold text-frenchBlue dark:text-white">
              {discount.value ?? '-'} {isPercentage ? '%' : currency}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscountItem;
